import { useState, useEffect } from 'react'
import { estimatesService } from '@/services/estimates-service'
import { clientsService } from '@/services/clients-service'
import type { Estimate, Client } from '@/types'

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
]

interface EditProjectDetailsModalProps {
  open: boolean
  onClose: () => void
  estimate: Estimate
  onSaved: (updated: Estimate) => void
}

export function EditProjectDetailsModal({ open, onClose, estimate, onSaved }: EditProjectDetailsModalProps) {
  const [activeTab, setActiveTab] = useState<'proposal' | 'client'>('proposal')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  // Proposal tab fields
  const [isPublicWorks, setIsPublicWorks] = useState(false)
  const [isUnitPricing, setIsUnitPricing] = useState(false)
  const [projectName, setProjectName] = useState('')
  const [supervisorName, setSupervisorName] = useState('')
  const [supervisorPhone, setSupervisorPhone] = useState('')
  const [supervisorEmail, setSupervisorEmail] = useState('')
  const [jobAddress, setJobAddress] = useState('')
  const [jobCity, setJobCity] = useState('')
  const [jobState, setJobState] = useState('')
  const [jobZip, setJobZip] = useState('')

  // Client tab fields
  const [clientName, setClientName] = useState('')
  const [clientEmail, setClientEmail] = useState('')
  const [clientPhone, setClientPhone] = useState('')
  const [clientCompany, setClientCompany] = useState('')
  const [clientIsResidence, setClientIsResidence] = useState(false)
  const [clientAddress, setClientAddress] = useState('')
  const [clientCity, setClientCity] = useState('')
  const [clientState, setClientState] = useState('')
  const [clientZip, setClientZip] = useState('')

  // Client record (if linked)
  const [client, setClient] = useState<Client | null>(null)

  // Load data when modal opens
  useEffect(() => {
    if (!open) return
    setActiveTab('proposal')
    setError('')

    // Populate proposal fields
    setIsPublicWorks(estimate.isPublicWorks || false)
    setIsUnitPricing(estimate.isUnitPricing || false)
    setProjectName(estimate.title || '')
    setSupervisorName(estimate.supervisorName || '')
    setSupervisorPhone(estimate.supervisorPhone || '')
    setSupervisorEmail(estimate.supervisorEmail || '')
    setJobAddress(estimate.jobAddress || '')
    setJobCity(estimate.jobCity || '')
    setJobState(estimate.jobState || '')
    setJobZip(estimate.jobZip || '')

    // Populate client fields from estimate
    setClientName(estimate.clientName || '')
    setClientEmail(estimate.clientEmail || '')
    setClientPhone(estimate.clientPhone || '')
    setClientCompany(estimate.clientCompany || '')
    setClientIsResidence(false)
    setClientAddress('')
    setClientCity('')
    setClientState('')
    setClientZip('')

    // If estimate has a linked client, load client details
    if (estimate.clientId) {
      clientsService.list().then((clients) => {
        const found = clients.find((c: Client) => c.id === estimate.clientId)
        if (found) {
          setClient(found)
          setClientName(found.name || '')
          setClientEmail(found.email || '')
          setClientPhone(found.phone || '')
          setClientCompany(found.company || '')
          setClientIsResidence(found.isResidence || false)
          setClientAddress(found.address || '')
          setClientCity(found.city || '')
          setClientState(found.state || '')
          setClientZip(found.zip || '')
        }
      }).catch(() => { /* ignore */ })
    }
  }, [open, estimate])

  async function handleSave() {
    setError('')
    if (!projectName.trim()) { setError('Project name is required'); setActiveTab('proposal'); return }
    if (!jobAddress.trim()) { setError('Job address is required'); setActiveTab('proposal'); return }
    if (!clientName.trim()) { setError('Client name is required'); setActiveTab('client'); return }

    setIsSaving(true)
    try {
      // Save estimate fields (proposal tab)
      const updated = await estimatesService.update(estimate.id, {
        title: projectName.trim(),
        isPublicWorks,
        isUnitPricing,
        supervisorName: supervisorName.trim(),
        supervisorPhone: supervisorPhone.trim() || undefined,
        supervisorEmail: supervisorEmail.trim() || undefined,
        jobAddress: jobAddress.trim(),
        jobCity: jobCity.trim(),
        jobState: jobState.trim(),
        jobZip: jobZip.trim(),
        clientName: clientName.trim(),
        clientEmail: clientEmail.trim() || undefined,
        clientPhone: clientPhone.trim() || undefined,
        clientCompany: clientCompany.trim() || undefined,
      })

      // Save client record if linked
      if (estimate.clientId && client) {
        await clientsService.update(estimate.clientId, {
          name: clientName.trim(),
          email: clientEmail.trim() || undefined,
          phone: clientPhone.trim() || undefined,
          company: clientCompany.trim() || undefined,
          isResidence: clientIsResidence,
          address: clientAddress.trim() || undefined,
          city: clientCity.trim() || undefined,
          state: clientState.trim() || undefined,
          zip: clientZip.trim() || undefined,
        }).catch(() => { /* client update is best-effort */ })
      }

      onSaved(updated)
      onClose()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save'
      setError(msg)
    } finally {
      setIsSaving(false)
    }
  }

  if (!open) return null

  return (
    <div className="epd-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="epd-dialog">
        {/* Header */}
        <div className="epd-header">
          <h2 className="epd-title">Edit Project Details</h2>
          <button className="epd-close" onClick={onClose}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="epd-tabs">
          <button
            className={`epd-tab ${activeTab === 'proposal' ? 'active' : ''}`}
            onClick={() => setActiveTab('proposal')}
          >
            Proposal
          </button>
          <button
            className={`epd-tab ${activeTab === 'client' ? 'active' : ''}`}
            onClick={() => setActiveTab('client')}
          >
            Client
          </button>
        </div>

        {/* Tab content */}
        <div className="epd-body">
          {activeTab === 'proposal' && (
            <div className="epd-form">
              <div className="wz-checkbox-row">
                <input
                  type="checkbox"
                  id="epd-public-works"
                  checked={isPublicWorks}
                  onChange={(e) => setIsPublicWorks(e.target.checked)}
                />
                <label htmlFor="epd-public-works">Public work</label>
              </div>
              <div className="wz-checkbox-row">
                <input
                  type="checkbox"
                  id="epd-unit-pricing"
                  checked={isUnitPricing}
                  onChange={(e) => setIsUnitPricing(e.target.checked)}
                />
                <label htmlFor="epd-unit-pricing">Unit Pricing Proposal</label>
              </div>

              <div className="wz-form-group">
                <label className="wz-label">Project Name *</label>
                <input
                  className="wz-input"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Project name"
                />
              </div>

              <div className="wz-form-row wz-form-row-3">
                <div className="wz-form-group">
                  <label className="wz-label">Supervisor Name *</label>
                  <input
                    className="wz-input"
                    value={supervisorName}
                    onChange={(e) => setSupervisorName(e.target.value)}
                    placeholder="Supervisor name"
                  />
                </div>
                <div className="wz-form-group">
                  <label className="wz-label">Phone</label>
                  <input
                    className="wz-input"
                    value={supervisorPhone}
                    onChange={(e) => setSupervisorPhone(e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </div>
                <div className="wz-form-group">
                  <label className="wz-label">Email</label>
                  <input
                    className="wz-input"
                    type="email"
                    value={supervisorEmail}
                    onChange={(e) => setSupervisorEmail(e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <div className="wz-form-group">
                <label className="wz-label">Address *</label>
                <input
                  className="wz-input"
                  value={jobAddress}
                  onChange={(e) => setJobAddress(e.target.value)}
                  placeholder="Street address"
                />
              </div>

              <div className="wz-form-row wz-form-row-3">
                <div className="wz-form-group">
                  <label className="wz-label">City *</label>
                  <input
                    className="wz-input"
                    value={jobCity}
                    onChange={(e) => setJobCity(e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="wz-form-group">
                  <label className="wz-label">State *</label>
                  <select
                    className="wz-select"
                    value={jobState}
                    onChange={(e) => setJobState(e.target.value)}
                  >
                    <option value="">State</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="wz-form-group">
                  <label className="wz-label">Zip Code *</label>
                  <input
                    className="wz-input"
                    value={jobZip}
                    onChange={(e) => setJobZip(e.target.value)}
                    placeholder="Zip"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'client' && (
            <div className="epd-form">
              <div className="wz-form-group">
                <label className="wz-label">Client Name *</label>
                <input
                  className="wz-input"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Client name"
                />
              </div>

              <div className="wz-form-row">
                <div className="wz-form-group">
                  <label className="wz-label">Email</label>
                  <input
                    className="wz-input"
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="wz-form-group">
                  <label className="wz-label">Phone Number</label>
                  <input
                    className="wz-input"
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </div>
              </div>

              <div className="wz-form-group">
                <label className="wz-label">Company Name</label>
                <input
                  className="wz-input"
                  value={clientCompany}
                  onChange={(e) => setClientCompany(e.target.value)}
                  placeholder="Company name"
                />
              </div>

              <div className="wz-checkbox-row">
                <input
                  type="checkbox"
                  id="epd-residence"
                  checked={clientIsResidence}
                  onChange={(e) => setClientIsResidence(e.target.checked)}
                />
                <label htmlFor="epd-residence">Residence Owner</label>
              </div>

              <div className="wz-form-group">
                <label className="wz-label">Address *</label>
                <input
                  className="wz-input"
                  value={clientAddress}
                  onChange={(e) => setClientAddress(e.target.value)}
                  placeholder="Street address"
                />
              </div>

              <div className="wz-form-row wz-form-row-3">
                <div className="wz-form-group">
                  <label className="wz-label">City *</label>
                  <input
                    className="wz-input"
                    value={clientCity}
                    onChange={(e) => setClientCity(e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="wz-form-group">
                  <label className="wz-label">State *</label>
                  <select
                    className="wz-select"
                    value={clientState}
                    onChange={(e) => setClientState(e.target.value)}
                  >
                    <option value="">State</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="wz-form-group">
                  <label className="wz-label">Zip Code *</label>
                  <input
                    className="wz-input"
                    value={clientZip}
                    onChange={(e) => setClientZip(e.target.value)}
                    placeholder="Zip"
                  />
                </div>
              </div>
            </div>
          )}

          {error && <p className="wz-error-text">{error}</p>}
        </div>

        {/* Footer */}
        <div className="epd-footer">
          <button className="wz-btn-outline" onClick={onClose}>Cancel</button>
          <button
            className="wz-btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
